import React, { useState, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../button/button.component';
import Icon from '../icon/icon.component';
import Link from '../link/link.component';
import TestId from '../../interfaces/test-id.interface';

interface MenuItem {
  /** The title to be rendered within the menu */
  title: string;

  /** The target url to link to / click handler to execute */
  target: string | MouseEventHandler;

  /** The name of the icon to render in front of the link text */
  icon: string;

  /** Indicator to highlight the currently active item */
  isActive?: boolean;
}

interface Props extends TestId {
  /** The components's contents */
  items: MenuItem[];

  /** Meta items are links which are rendered on the bottom of the menu */
  metaItems?: (Pick<MenuItem, 'title'> & { target: string })[];
}

/** A menu used to render the main navigation */
const Menu = ({ items, metaItems, testId }: Props): React.ReactElement => {
  const [isOpen, setIsOpen] = useState<boolean>();
  const { t } = useTranslation();

  return (
    <div
      className="menu flex flex-col border-t border-gray-200 h-full justify-between"
      data-testid={testId}
    >
      <nav>
        {items.map((item) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const El = (props: any): React.ReactElement =>
            typeof item.target === 'function' ? (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <Button variant="plain" onClick={item.target} {...props} />
            ) : (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <Link variant="link" href={item.target} {...props} />
            );

          return (
            <El
              key={item.title}
              display="flex"
              className={`
                border-l-4
                hover:border-teal-600
                hover:text-black
                items-center
                my-3
                px-3
                py-2
                leading-none
                ${item.isActive ? 'border-teal-600 text-black' : 'border-transparent'}
              `}
            >
              <Icon name={item.icon} variant={isOpen ? 'pretext' : 'plain'} />
              <span className={isOpen ? 'pr-6 text-sm' : 'hidden'}>{item.title}</span>
            </El>
          );
        })}
      </nav>

      <div>
        {metaItems && (
          <div className={`${isOpen ? 'block' : 'hidden'} text-xs text-gray-500`}>
            {metaItems.map((item, i) => (
              <Link
                key={item.target}
                href={item.target}
                target="_blank"
                display="inline-block"
                variant="link"
                className={`leading-none px-3 border-gray-300 ${i !== 0 ? 'border-l' : ''}`}
              >
                {item.title}
              </Link>
            ))}
          </div>
        )}
        <Button
          className="px-3 py-1 my-5"
          display="block"
          title={t(`${isOpen ? 'open' : 'close'}`)}
          onClick={(): void => setIsOpen((state) => !state)}
          variant="plain"
        >
          <Icon name={isOpen ? 'angle-left' : 'bars'} variant="plain" />
        </Button>
      </div>
    </div>
  );
};

export default Menu;
